import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { CATS, TX_LOCS, locName } from '../lib/constants';
import PublicHeader from '../components/PublicHeader';
import StarRating from '../components/StarRating';

function VehicleCard({ c }) {
  return (
    <Link
      to={`/cars/${c.id}`}
      data-testid={`car-card-${c.id}`}
      style={{
        display: 'block',
        background: '#fff',
        borderRadius: 16,
        border: '1px solid #E2E8F0',
        overflow: 'hidden',
        transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 14px 30px rgba(13,27,62,.08)';
        e.currentTarget.style.borderColor = '#0D1B3E';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = '#E2E8F0';
      }}
    >
      <div
        style={{
          height: 180,
          background: `linear-gradient(135deg,${c.color}22,${c.color}44)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {c.img ? (
          <img
            src={c.img}
            alt={`${c.brand} ${c.model}`}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          style={{
            display: c.img ? 'none' : 'flex',
            fontSize: 80,
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {c.emoji}
        </span>
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: c.color,
            color: '#fff',
            borderRadius: 20,
            padding: '4px 12px',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '.04em',
            textTransform: 'uppercase',
          }}
        >
          {c.cat}
        </div>
        {c.status !== 'Available' && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              background: 'rgba(15,23,42,.85)',
              color: '#fff',
              borderRadius: 20,
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {c.status}
          </div>
        )}
      </div>
      <div style={{ padding: 18 }}>
        <div style={{ fontWeight: 900, fontSize: 18, color: '#0F172A', lineHeight: 1.2 }}>
          {c.brand} {c.model}
        </div>
        <div style={{ color: '#64748B', fontSize: 12, marginTop: 4 }}>
          {c.yr} · {c.trans} · {c.fuel}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 14 }}>
          <span style={{ fontWeight: 900, fontSize: 28, color: '#0D1B3E' }}>${c.ppd}</span>
          <span style={{ fontSize: 13, color: '#94A3B8' }}>/day</span>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 14, color: '#64748B', fontSize: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>👥 {c.seats} seats</span>
          <span>🧳 {c.bags} bags</span>
          <span>⛽ {c.mpg} mpg</span>
        </div>
        {(c.review_count > 0 || c.avg_rating) && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #F1F5F9' }}>
            <StarRating value={c.avg_rating || 0} count={c.review_count || 0} size={13} />
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Landing() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('All');
  const [loc, setLoc] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    api
      .get('/vehicles')
      .then((r) => setVehicles(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = vehicles.filter((c) => {
    const q = search.toLowerCase();
    return (
      (!q || `${c.brand} ${c.model} ${c.cat}`.toLowerCase().includes(q)) &&
      (cat === 'All' || c.cat === cat) &&
      (loc === 'All' || (c.locs || []).includes(loc))
    );
  });

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <PublicHeader />

      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #0D1B3E 0%, #142554 60%, #1E3A8A 100%)',
          color: '#fff',
          padding: '60px 24px 80px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, background: 'radial-gradient(circle, rgba(230,57,70,.25), transparent 70%)' }} />
        <div style={{ maxWidth: 1240, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <span
            className="fade-up"
            style={{
              display: 'inline-block',
              background: 'rgba(230,57,70,.18)',
              color: '#fca5a5',
              borderRadius: 20,
              padding: '5px 14px',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              marginBottom: 18,
            }}
          >
            Texas-wide · 15 Pickup Locations
          </span>
          <h1
            className="fade-up-delay-1"
            style={{
              fontSize: 'clamp(36px, 5vw, 60px)',
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: '-.025em',
              margin: 0,
              maxWidth: 820,
            }}
          >
            Drive Texas. <span style={{ color: '#E63946' }}>Your way.</span>
          </h1>
          <p
            className="fade-up-delay-2"
            style={{ fontSize: 18, color: 'rgba(255,255,255,.75)', marginTop: 18, maxWidth: 620, lineHeight: 1.6 }}
          >
            From compact economy to electric SUVs and American muscle — pick your ride, choose your city, hit the road.
          </p>

          {/* Search bar */}
          <div
            data-testid="search-bar"
            style={{
              marginTop: 36,
              background: '#fff',
              borderRadius: 16,
              padding: 14,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr auto',
              gap: 10,
              boxShadow: '0 20px 60px rgba(0,0,0,.25)',
            }}
          >
            <input
              data-testid="hero-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search brand, model..."
              style={{ padding: '12px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 14 }}
            />
            <select
              data-testid="hero-cat"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              style={{ padding: '12px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 14 }}
            >
              <option value="All">All categories</option>
              {CATS.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <select
              data-testid="hero-loc"
              value={loc}
              onChange={(e) => setLoc(e.target.value)}
              style={{ padding: '12px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 14 }}
            >
              <option value="All">All locations</option>
              {TX_LOCS.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            <button data-testid="hero-search-btn" className="btn-red" style={{ padding: '12px 26px' }}>
              Search
            </button>
          </div>
        </div>
      </section>

      {/* Stat strip */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E2E8F0' }}>
        <div
          style={{
            maxWidth: 1240,
            margin: '0 auto',
            padding: '24px',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 24,
            textAlign: 'center',
          }}
        >
          {[
            ['🚗', `${vehicles.length}+`, 'Vehicles in fleet'],
            ['📍', '15', 'Texas locations'],
            ['⭐', '4.8/5', 'Customer rating'],
            ['💸', '$0', 'Hidden fees'],
          ].map(([ic, v, l]) => (
            <div key={l}>
              <div style={{ fontSize: 22 }}>{ic}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#0D1B3E', marginTop: 4 }}>{v}</div>
              <div style={{ fontSize: 12, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Fleet grid */}
      <section data-testid="fleet-section" style={{ maxWidth: 1240, margin: '0 auto', padding: '48px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 32, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-.02em' }}>
              {filtered.length === vehicles.length ? 'Our Fleet' : `${filtered.length} vehicle${filtered.length === 1 ? '' : 's'} available`}
            </h2>
            <p style={{ color: '#64748B', marginTop: 6 }}>
              {cat !== 'All' && `${cat} · `}
              {loc !== 'All' && `${locName(loc)}`}
              {cat === 'All' && loc === 'All' && 'Pick a ride that fits your trip.'}
            </p>
          </div>
          {(cat !== 'All' || loc !== 'All' || search) && (
            <button
              data-testid="clear-filters-btn"
              className="btn-ghost"
              onClick={() => {
                setCat('All');
                setLoc('All');
                setSearch('');
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: '#94A3B8' }}>Loading fleet…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, color: '#94A3B8', background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0' }}>
            No vehicles match your filters.
          </div>
        ) : (
          <div data-testid="fleet-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 22 }}>
            {filtered.map((c) => (
              <VehicleCard key={c.id} c={c} />
            ))}
          </div>
        )}
      </section>

      {/* Why section */}
      <section style={{ background: '#fff', borderTop: '1px solid #E2E8F0' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '64px 24px' }}>
          <h2 style={{ fontSize: 28, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-.02em' }}>
            Why RentCarsUS
          </h2>
          <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
            {[
              ['🚀', 'Quick checkout', '5-minute pickup. No paperwork tornado.'],
              ['🛡️', 'All-inclusive', 'Insurance, roadside, mileage — all in.'],
              ['🤠', 'Texas-built', 'Local team, local fleet, statewide reach.'],
              ['⚡', 'EVs ready', 'Tesla, Polestar, Rivian — charged & set.'],
            ].map(([ic, t, d]) => (
              <div key={t} className="card">
                <div style={{ fontSize: 28 }}>{ic}</div>
                <div style={{ fontWeight: 900, fontSize: 16, marginTop: 8, color: '#0F172A' }}>{t}</div>
                <div style={{ color: '#64748B', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer style={{ background: '#0D1B3E', color: 'rgba(255,255,255,.6)', padding: '36px 24px', fontSize: 13 }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <span>© 2026 RentCarsUS — Drive Texas, Your Way.</span>
          <Link to="/admin" data-testid="footer-admin-link" style={{ color: 'rgba(255,255,255,.7)' }}>
            Admin Login →
          </Link>
        </div>
      </footer>
    </div>
  );
}
